import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Icon } from '@st-operations/uikit/src/v2'

// ---- helpers ---------------------------------------------------------------

const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v)
const isPrimitive = (v) => v === null || typeof v !== 'object'

// Deterministic JSON used for equality. Object keys are sorted, and arrays are
// compared order-insensitively so a reordered-but-identical list is "unchanged".
const stableStringify = (value) => {
  const recurse = (v) => {
    if (v === null || typeof v !== 'object') return v
    if (Array.isArray(v)) {
      return v
        .map(recurse)
        .map((x) => JSON.stringify(x))
        .sort()
    }
    return Object.keys(v)
      .sort()
      .reduce((acc, key) => {
        acc[key] = recurse(v[key])
        return acc
      }, {})
  }
  return JSON.stringify(recurse(value))
}

const deepEqual = (a, b) => stableStringify(a) === stableStringify(b)

// The audit response is camelized on the way in (requests.js), so nested keys
// arrive as camelCase, not snake_case. Split on both a camel-hump and an
// underscore before title-casing, so hasCountryRestrictions and, defensively,
// any surviving snake_case key both read as words.
const humanize = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const formatScalar = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const itemLabel = (item) => item?.name || item?.label || item?._id || 'item'

const formatList = (arr) =>
  arr && arr.length
    ? arr.map((v) => (isPrimitive(v) ? formatScalar(v) : itemLabel(v))).join(', ')
    : '—'

const arrayOfObjectsWithId = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  arr.every((x) => isPlainObject(x) && x._id !== undefined && x._id !== null)

const formatLeaf = (value) => {
  if (isPrimitive(value)) return formatScalar(value)
  if (Array.isArray(value)) return formatList(value)
  return JSON.stringify(value)
}

// ---- diff (data) -----------------------------------------------------------
// Builds a pruned tree of only what changed. Returns null when nothing
// meaningful changed, so empty fields/properties are dropped entirely.
// Node shapes:
//   { type: 'leaf',   label, from, to }
//   { type: 'item',   kind: 'added' | 'removed', label }
//   { type: 'branch', label, count, children: [node] }
// A newly-set field records from: null (and a cleared one, to: null). Coerce the
// absent side to the empty shape of its counterpart so the structural diff below
// runs — a bare null against an object would otherwise fall through to a raw,
// clipped JSON.stringify.
const coerceEmptyLike = (value, counterpart) => {
  if (value !== null && value !== undefined) return value
  if (isPlainObject(counterpart)) return {}
  if (Array.isArray(counterpart)) return []
  return value
}

const buildDiff = (label, rawFrom, rawTo) => {
  // Equal values (e.g. after stale items were filtered out) contribute nothing.
  if (deepEqual(rawFrom, rawTo)) return null

  const from = coerceEmptyLike(rawFrom, rawTo)
  const to = coerceEmptyLike(rawTo, rawFrom)

  if (isPlainObject(from) && isPlainObject(to)) {
    const children = []
    Object.keys(to).forEach((key) => {
      // Only consider keys present on the new value; keys that exist solely on
      // the previous value are payload-shape artifacts, not real removals.
      if (key === '_id' || deepEqual(from[key], to[key])) return
      const child = buildDiff(humanize(key), from[key], to[key])
      if (child) children.push(child)
    })
    if (!children.length) return null
    return { type: 'branch', label, count: children.length, children }
  }

  if (
    Array.isArray(from) &&
    Array.isArray(to) &&
    (arrayOfObjectsWithId(from) || arrayOfObjectsWithId(to))
  ) {
    const fromMap = new Map(from.filter(isPlainObject).map((x) => [String(x._id), x]))
    const toMap = new Map(to.filter(isPlainObject).map((x) => [String(x._id), x]))
    const children = []

    toMap.forEach((item, id) => {
      if (!fromMap.has(id)) children.push({ type: 'item', kind: 'added', label: itemLabel(item) })
    })
    fromMap.forEach((item, id) => {
      if (!toMap.has(id)) children.push({ type: 'item', kind: 'removed', label: itemLabel(item) })
    })
    toMap.forEach((item, id) => {
      if (!fromMap.has(id) || deepEqual(fromMap.get(id), item)) return
      const child = buildDiff(itemLabel({ ...fromMap.get(id), ...item }), fromMap.get(id), item)
      if (child) children.push(child)
    })

    if (!children.length) return null
    return { type: 'branch', label, count: children.length, children }
  }

  return { type: 'leaf', label, from: formatLeaf(from), to: formatLeaf(to) }
}

// ---- render ----------------------------------------------------------------

const Leaf = ({ label, from, to }) => (
  <li className="value-diff__leaf">
    <If condition={label}>
      <span className="value-diff__key">{label}</span>
    </If>
    <span className="value-diff__from">{from}</span>
    <span className="value-diff__arrow">→</span>
    <span className="value-diff__to">{to}</span>
  </li>
)

const ItemRow = ({ kind, label }) => (
  <li className={`value-diff__item value-diff__item--${kind}`}>
    <span className="value-diff__sign">{kind === 'added' ? '+' : '−'}</span>
    <span>{label}</span>
  </li>
)

const Branch = ({ node }) => {
  const [open, setOpen] = useState(true)
  return (
    <li className="value-diff__branch">
      <button
        type="button"
        className="value-diff__toggle"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Icon
          icon={open ? 'angledown' : 'angleright'}
          type="outline"
          height="14px"
          width="14px"
          color="#3d3d3d"
        />
        <span className="value-diff__key">{node.label}</span>
        <If condition={!open}>
          <span className="value-diff__count">
            {node.count} {node.count === 1 ? 'change' : 'changes'}
          </span>
        </If>
      </button>
      <If condition={open}>
        <ul className="value-diff__tree">
          <For each="child" index="i" of={node.children}>
            <DiffNode key={i} node={child} />
          </For>
        </ul>
      </If>
    </li>
  )
}

const DiffNode = ({ node }) => {
  if (node.type === 'leaf') return <Leaf label={node.label} from={node.from} to={node.to} />
  if (node.type === 'item') return <ItemRow kind={node.kind} label={node.label} />
  return <Branch node={node} />
}

const ValueDiff = ({ label, from, to }) => {
  const node = buildDiff(label, from, to)
  if (!node) return null
  return <DiffNode node={node} />
}

const nodeShape = PropTypes.object

Leaf.propTypes = { label: PropTypes.string, from: PropTypes.node, to: PropTypes.node }
ItemRow.propTypes = {
  kind: PropTypes.oneOf(['added', 'removed']).isRequired,
  label: PropTypes.node,
}
Branch.propTypes = { node: nodeShape.isRequired }
DiffNode.propTypes = { node: nodeShape.isRequired }
ValueDiff.propTypes = { label: PropTypes.string, from: PropTypes.any, to: PropTypes.any }

export default ValueDiff
